import { Grid, Typography } from '@material-ui/core'
import React from 'react'
// TODO: this should not be in shared
import { DateTimeInput, NumberInput, SelectInput, TextInput, useNotify, useRedirect } from 'react-admin'
import { Form, FormRenderProps } from 'react-final-form'
import { Accordion } from 'shared/components/Accordion/Accordion'
import { Card } from 'shared/components/Base/Base'
import { FlexBox } from 'shared/components/Base/FlexBox'
import { Box } from 'shared/components/Box/Box'
import { Button } from 'shared/components/Button/Button'
import { Text } from 'shared/components/Text/Text'
import { formatCurrency } from 'shared/helpers/formatters'
import { Handler } from 'shared/helpers/typeHelper'
import { sizes } from 'shared/theme'
import { formStroke, lightGray1, midGray, primaryBlack } from 'shared/theme/colors'
import styled from 'styled-components'

import {
  createPolicyDocument,
  createRepaymentPolicy,
  CreateUpdatePolicyPayload,
  formatPeriodicAmount,
  ONLY_COLLECTION_POLICY,
  ONLY_RECIPIENT_TYPE,
  PolicyDocument,
  RepaymentPolicyStatus,
  TrucksAndTrailersChoice,
  updateRepaymentPolicy,
  VendorProgram,
  VendorProgramPolicyType,
  VendorProgramSubheaderType,
} from '../../../models/repayments'
import { ConsoleTeam } from '../../../models/team'
import { PolicyDocuments } from './PolicyDocuments'
import { PolicySubheader } from './PolicySubheader'

const SCHEDULE_CHOICES = [
  { id: 'WEEKLY', name: 'Weekly' },
  { id: 'MONTHLY', name: 'Monthly' },
]

export function PolicyFormDetails({
  secondStep,
  activeVendorProgram,
  team,
  isEditing,
  editingPolicyId,
  initialValues,
  policyDocuments,
  refetchDocuments,
  refetchPolicy,
}: {
  secondStep: boolean
  activeVendorProgram?: VendorProgram
  team: ConsoleTeam
  isEditing?: boolean
  editingPolicyId?: string
  initialValues?: any
  policyDocuments?: PolicyDocument[]
  refetchDocuments?: Handler
  refetchPolicy?: Handler
}) {
  return (
    <Card>
      <Accordion
        isExpanded={secondStep}
        isDisabled
        details={
          <AccordionDetails
            isEditing={isEditing}
            editingPolicyId={editingPolicyId}
            activeVendorProgram={activeVendorProgram}
            team={team}
            initialValues={initialValues}
            policyDocuments={policyDocuments}
            refetchDocuments={refetchDocuments}
            refetchPolicy={refetchPolicy}
          />
        }
      >
        {() => (
          <FlexBox justifyContent="flex-start" alignItems="flex-start">
            <Text variant="header2" fontColor={!secondStep ? lightGray1 : primaryBlack}>
              Deduction details
            </Text>
          </FlexBox>
        )}
      </Accordion>
    </Card>
  )
}

function AccordionDetails({
  activeVendorProgram,
  team,
  isEditing,
  editingPolicyId,
  initialValues,
  policyDocuments,
  refetchDocuments,
  refetchPolicy,
}: {
  activeVendorProgram?: VendorProgram
  team: ConsoleTeam
  isEditing?: boolean
  editingPolicyId?: string
  initialValues?: any
  policyDocuments?: PolicyDocument[]
  refetchDocuments?: Handler
  refetchPolicy?: Handler
}) {
  const notify = useNotify()
  const redirect = useRedirect()
  const [draftDocuments, setDraftDocuments] = React.useState<File[]>([])
  const [isLoading, setIsLoading] = React.useState(false)

  if (!activeVendorProgram) {
    return null
  }

  const trucksAndTrailersChoices: TrucksAndTrailersChoice[] = [
    ...team.trucks.map((truck) => ({
      id: truck.id,
      name: `${truck.license_plate_state}-${truck.license_plate_number} (${truck.vin_number})`,
      vin_number: truck.vin_number ?? '',
      license_plate_number: truck.license_plate_number ?? '',
    })),
    ...team.trailers.map((trailer) => ({
      id: trailer.id,
      name: `${trailer.license_plate_state}-${trailer.license_plate_number} (${trailer.vin_number})`,
      vin_number: trailer.vin_number ?? '',
      license_plate_number: trailer.license_plate_number ?? '',
    })),
  ]

  return (
    <Grid>
      <FlexBox isColumn alignItems="stretch" mt={4} gap="20px">
        <FlexBox alignItems="flex-start">
          <Text variant="paragraph2" fontColor={midGray}>
            Add details about the deduction. These will be visible to the user.
          </Text>
        </FlexBox>

        <Form
          initialValues={initialValues}
          render={(props) => (
            <FormRender
              {...props}
              vendorProgram={activeVendorProgram}
              draftDocuments={draftDocuments}
              setDraftDocuments={setDraftDocuments}
              policyDocuments={policyDocuments}
              refetchDocuments={refetchDocuments}
              trucksAndTrailersChoices={trucksAndTrailersChoices}
              actions={
                <FlexBox justifyContent="flex-end" alignItems="center" gap="20px">
                  <Button size={sizes.large} type="submit" onClick={props.handleSubmit} isLoading={isLoading}>
                    {isEditing ? 'SAVE' : 'SUBMIT'}
                  </Button>
                </FlexBox>
              }
            />
          )}
          onSubmit={(data) => {
            // TODO: convert this to an async function
            setIsLoading(true)

            const policyPayload: CreateUpdatePolicyPayload = {
              policy_fields: {
                ...(activeVendorProgram.subheader === VendorProgramSubheaderType.TRAILER_ID
                  ? {
                      vin_number: trucksAndTrailersChoices.find((t) => t.vin_number === data.trailer_id)
                        ?.vin_number,
                    }
                  : {}),
                ...(activeVendorProgram.subheader === VendorProgramSubheaderType.VIN_NUMBER
                  ? {
                      vin_number: trucksAndTrailersChoices.find((t) => t.vin_number === data.vin_number)
                        ?.vin_number,
                    }
                  : {}),
                ...(activeVendorProgram.subheader === VendorProgramSubheaderType.PLATES_NUMBER
                  ? {
                      vin_number: trucksAndTrailersChoices.find(
                        (t) => t.license_plate_number === data.plates_number
                      )?.vin_number,
                    }
                  : {}),
                collection_policy: ONLY_COLLECTION_POLICY,
                description: data.payment_description,
                num_periods:
                  activeVendorProgram.policy_type === VendorProgramPolicyType.SUBSCRIPTION
                    ? -1
                    : data.number_of_payment_periods,
                payment_start_date: data.start_date,
                periodic_amount: formatPeriodicAmount(data.advanced_amount, data.number_of_payment_periods),
                periodic_payment_interval: data.payment_schedule,
                status: RepaymentPolicyStatus.DRAFT,
                sub_header: data.sub_header,
                team_id: team.id,
              },
              recipient_type: ONLY_RECIPIENT_TYPE,
              vendor_program: activeVendorProgram?.name,
            }

            return (
              isEditing && editingPolicyId
                ? updateRepaymentPolicy(editingPolicyId, policyPayload)
                : createRepaymentPolicy(policyPayload)
            )
              .then(({ policy_id }) => {
                refetchPolicy?.()
                draftDocuments.forEach(async (file) => {
                  await createPolicyDocument(policy_id, file)
                })
                return policy_id
              })
              .then((policy_id) => redirect(`/teams/${team.id}/policy/details/${policy_id}`))
              .catch((error) => {
                notify(`Oops! Something went wrong: ${JSON.stringify(error.response.data)}`, {
                  type: 'error',
                })
              })
              .finally(() => {
                setIsLoading(false)
              })
          }}
        />
      </FlexBox>
    </Grid>
  )
}

function FormRender({
  vendorProgram,
  actions,
  trucksAndTrailersChoices,
  draftDocuments,
  setDraftDocuments,
  policyDocuments,
  refetchDocuments,
  form,
  values,
}: {
  vendorProgram: VendorProgram
  actions: React.ReactNode
  trucksAndTrailersChoices: TrucksAndTrailersChoice[]
  draftDocuments?: File[]
  setDraftDocuments: (documents: File[]) => void
  policyDocuments?: PolicyDocument[]
  refetchDocuments?: Handler
} & FormRenderProps<Record<string, any>>) {
  return (
    <Container isColumn>
      <Container isColumn alignItems="stretch" width="750px" mb={8}>
        <PolicySubheader
          vendorProgram={vendorProgram}
          subheaderValue={values.sub_header}
          setSubheader={(subheader: string) => form.change('sub_header', subheader)}
          trucksAndTrailersChoices={trucksAndTrailersChoices}
        />

        <StyledTextInput source="payment_description" label="Deduction description" multiline />

        <DateTimeInput source="start_date" label="Start date" />

        {vendorProgram.policy_type === VendorProgramPolicyType.ADVANCE && (
          <TextInput
            source="advanced_amount"
            label="Advance amount"
            InputProps={{
              startAdornment: (
                <Typography variant="subtitle1" style={{ alignSelf: 'flex-end' }}>
                  $
                </Typography>
              ),
            }}
          />
        )}

        <FlexBox justifyContent="space-between" alignItems="center" gap="20px">
          <SelectInput
            style={{ flex: 1 }}
            source="payment_schedule"
            label="Billing schedule"
            choices={SCHEDULE_CHOICES}
          />

          {vendorProgram.policy_type === VendorProgramPolicyType.ADVANCE ? (
            <NumberInput
              style={{ flex: 1 }}
              source="number_of_payment_periods"
              label="Number of billing periods"
              // prevent user from entering e, in the number input, this is the most compact solution
              onKeyDown={(e) => e.keyCode === 69 && e.preventDefault()}
            />
          ) : (
            <TextInput
              style={{ flex: 1 }}
              source="advanced_amount"
              label="Billing amount"
              InputProps={{
                startAdornment: (
                  <Typography variant="subtitle1" style={{ alignSelf: 'flex-end' }}>
                    $
                  </Typography>
                ),
              }}
            />
          )}
        </FlexBox>

        {vendorProgram.policy_type === VendorProgramPolicyType.ADVANCE && (
          <FlexBox mt={2} gap="16px">
            <Typography variant="h6" style={{ color: lightGray1 }}>
              Billing amount per period:
            </Typography>
            <Typography variant="h6">
              {Boolean(values.advanced_amount) && Boolean(values.number_of_payment_periods)
                ? formatCurrency(
                    parseFloat(values.advanced_amount) / parseFloat(values.number_of_payment_periods)
                  )
                : formatCurrency(0)}
            </Typography>
          </FlexBox>
        )}

        <Box style={{ borderTop: `1px solid ${formStroke}` }} mt={4} mb={4} />

        <PolicyDocuments
          draft
          policyDocuments={policyDocuments}
          refetchDocuments={refetchDocuments}
          draftDocuments={draftDocuments}
          setDraftDocuments={setDraftDocuments}
        />
      </Container>

      {actions}
    </Container>
  )
}

// select all div descendants recursively
const Container = styled(FlexBox)`
  & div {
    display: flex;
  }
`

const StyledTextInput = styled(TextInput)`
  & > div {
    min-height: 100px;
  }
`
